import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Event as EventIcon, Person as PersonIcon } from "@mui/icons-material";
import { findMySignedUpLesson } from "../SignUpForLessonForm";
import StoreModal from "../../StoreModal";
import LoginModal from "../../Login";
import { useAuthStore } from "../../../store";
import "../../../App.css";

// Import utilities
import {
  MONTHS_PL,
  AcceptRegulaminLabel,
  payDialogOn,
  DUO_PASS_NAME,
  DUO_SPOT_PASS_NAME,
} from "./utils/constants";
import { formatDateLine } from "./utils/formatters";

// Import dialogs
import AlreadySignedUpDialog from "./dialogs/AlreadySignedUpDialog";
import SimpleConfirmDialog from "./dialogs/SimpleConfirmDialog";
import PaymentChoiceDialog from "./dialogs/PaymentChoiceDialog";
import UnpaidDebtDialog from "./dialogs/UnpaidDebtDialog";

// Import components
import LessonLocation from "./components/LessonLocation";
import PassSelector from "./components/PassSelector";
import PaymentSelector from "./components/PaymentSelector";
import FriendSelector from "./components/FriendSelector";
import GroupSummary from "./components/GroupSummary";
import StatuteHighlights from "./components/StatuteHighlights";
import DuoModePicker from "./components/DuoModePicker";

// Import hooks
import { useLessonData } from "./hooks/useLessonData";
import { useSignUpLogic } from "./hooks/useSignUpLogic";

const SignUpForLessonModal = ({ open, onClose, setNotification, lessonData }) => {
  // Auth store
  const {
    isAuthenticated,
    addLesson,
    email,
    name,
    lastName,
    phone,
    user,
    fetchLessons,
    fetchPendingSignups,
    markPayOnSite,
  } = useAuthStore((state) => ({
    isAuthenticated: state.isAuthenticated,
    addLesson: state.addLesson,
    email: state.email,
    name: state.name,
    lastName: state.lastName,
    phone: state.phone,
    user: state.user,
    fetchLessons: state.fetchLessons,
    fetchPendingSignups: state.fetchPendingSignups,
    markPayOnSite: state.markPayOnSite,
  }));

  // Local state
  const [error, setError] = useState("");
  // My entry from "my lessons" when I already hold a seat here (else null).
  const [alreadySignedUp, setAlreadySignedUp] = useState(null);
  const [formValues, setFormValues] = useState({ signInMethod: "", selectedPass: null });
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [acceptRegulamin, setAcceptRegulamin] = useState(false);
  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [eventPasses, setEventPasses] = useState([]);
  const [eventStoreModalOpen, setEventStoreModalOpen] = useState(false);
  // Group booking: friends booked together with the user, the backend preview
  // of who pays for which seat, and what the payment dialog should charge.
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [groupPreview, setGroupPreview] = useState(null);
  const [payment, setPayment] = useState({ amount: null, label: "Koszt zajęć" });
  // Duo/trio with single spots: "whole" = book the whole lesson, "spot" = one spot.
  const [duoMode, setDuoMode] = useState("whole");

  // Custom hooks
  const { isDataLoading, passes, canSignData, fetchCanSignData } = useLessonData(
    open,
    isAuthenticated,
    user,
    lessonData
  );

  const {
    loading,
    setLoading,
    signingOut,
    handleUserToLesson,
    handleSignOutLesson,
    handleRemoveFromWaitlist,
    handlePayment,
    handlePayDebt
  } = useSignUpLogic(
    user,
    email,
    name,
    lastName,
    phone,
    isAuthenticated,
    addLesson,
    setNotification
  );

  // Theme
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Computed values
  const header = `Zapisz się na ${lessonData.lesson_name || ""}`;
  const dateLine = formatDateLine(lessonData.lesson_start, lessonData.lesson_week_day, MONTHS_PL);
  const trainerName = lessonData.trainer?.full_name || "";
  const perBooking = lessonData?.lesson_type?.participants_per_booking;
  const isDuo = perBooking >= 2;
  const price = lessonData?.lesson_type?.price || 0;
  // Duo/trio may also be booked one spot at a time (spot_price set on the type).
  const spotPrice = isDuo ? (canSignData.spot_price ?? lessonData?.lesson_type?.spot_price ?? null) : null;
  const hasSpotMode = spotPrice != null;
  const showDuoPicker = hasSpotMode && !canSignData.duo_booked && !canSignData.is_on_waitlist;
  const isSpot = hasSpotMode && duoMode === "spot";
  const modePrice = isSpot ? spotPrice : (isDuo ? (canSignData.whole_price ?? price) : price);
  // Make-up passes are mode specific and never auto-applied.
  const duoPasses = isDuo
    ? passes.filter((p) => p.pass_type_name === (isSpot ? DUO_SPOT_PASS_NAME : DUO_PASS_NAME))
    : [];
  const maxFriends = isDuo
    ? (isSpot ? 0 : perBooking - 1)
    : Math.max(0, (lessonData?.lesson_type?.max_people || 0) - (lessonData?.current_people || 0) - 1);

  // Effects
  useEffect(() => {
    if (!open) {
      setAlreadySignedUp(null);
      setEventPasses([]);
      return;
    }

    const init = async () => {
      setFormValues({ signInMethod: isDuo ? "pojedyncza" : "", selectedPass: null });
      setSelectedFriends([]);
      setGroupPreview(null);
      await fetchLessons();
      setAlreadySignedUp(findMySignedUpLesson(lessonData.lesson_id));
      
      // Fetch available event passes if this lesson is part of an event
      if (lessonData.event_id && isAuthenticated && user) {
        try {
          const idToken = await user.getIdToken();
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/for-event/${lessonData.event_id}`,
            { headers: { Authorization: `Bearer ${idToken}` } }
          );
          const data = await response.json();
          if (Array.isArray(data.objects)) {
            setEventPasses(data.objects);
          } else {
            setEventPasses([]);
          }
        } catch (err) {
          console.error('Error fetching event passes:', err);
          setEventPasses([]);
        }
      } else {
        setEventPasses([]);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAuthenticated]);

  // Default mode: the whole booking while it is still possible, otherwise a spot.
  useEffect(() => {
    if (!hasSpotMode) {
      setDuoMode("whole");
      return;
    }
    setDuoMode(canSignData.whole_available !== false ? "whole" : "spot");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSignData, hasSpotMode]);

  const changeDuoMode = (mode) => {
    if (mode === duoMode) return;
    setDuoMode(mode);
    // Passes and friends are mode specific; start over.
    setFormValues((prev) => ({ ...prev, signInMethod: "pojedyncza", selectedPass: null }));
    setSelectedFriends([]);
  };

  const duoModePicker = showDuoPicker ? (
    <DuoModePicker
      value={duoMode}
      onChange={changeDuoMode}
      perBooking={perBooking}
      wholePrice={canSignData.whole_price ?? price}
      spotPrice={spotPrice}
      freeSpots={canSignData.free_spots}
      wholeAvailable={canSignData.whole_available !== false}
      spotAvailable={Boolean(canSignData.spot_available)}
    />
  ) : null;

  // Joining the waitlist of a duo/trio is only possible for a single spot.
  const effectiveDuoMode = hasSpotMode && !canSignData.available ? "spot" : duoMode;

  useEffect(() => {
    if (!open || !user || selectedFriends.length === 0) {
      setGroupPreview(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const idToken = await user.getIdToken();
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/user_to_lesson/group-preview`, {
          method: "POST",
          headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
          body: JSON.stringify({ lesson_id: lessonData.lesson_id, friend_ids: selectedFriends }),
        });
        if (resp.ok && !cancelled) setGroupPreview(await resp.json());
      } catch {
        // Preview is informational; the signup itself still decides.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user, selectedFriends]);

  const friendSelector = maxFriends > 0 || selectedFriends.length > 0 ? (
    <FriendSelector
      user={user}
      maxFriends={maxFriends}
      selected={selectedFriends}
      onChange={setSelectedFriends}
      isDuo={isDuo}
    />
  ) : null;

  // With friends picked the button says how many people get booked.
  const people = selectedFriends.length + 1;
  const submitLabel = selectedFriends.length
    ? `Zapisz ${people} ${people % 10 >= 2 && people % 10 <= 4 && (people % 100 < 12 || people % 100 > 14) ? "osoby" : "osób"}`
    : "Zapisz się";

  // Who pays for which seat: duo/trio and free lessons have one price (or none) for all.
  const showGroupSummary = selectedFriends.length > 0 && !isDuo && !lessonData?.is_free;
  const ownSeat = {
    karnet: { amount: 0, note: "z karnetu" },
    pojedyncza: { amount: price },
    kup_karnet: { amount: null, note: "kupisz karnet" },
  }[formValues.signInMethod] || { amount: null, note: "najpierw wybierz płatność ↑", pending: true };
  // Simple flows (pay on site / online only) have no choice to make for my seat.
  const simpleExtra = (totalLabel) => (
    <>
      {duoModePicker}
      {friendSelector}
      {showGroupSummary && <GroupSummary preview={groupPreview} own={{ amount: price }} totalLabel={totalLabel} />}
    </>
  );

  // Flow handlers
  const handleFreeLesson = async () => {
    setLoading(true);
    const ok = await handleUserToLesson(lessonData, formValues, fetchCanSignData, selectedFriends, effectiveDuoMode);
    setLoading(false);
    if (ok) onClose();
  };

  const handlePaymentOnSite = async () => {
    setLoading(true);
    const ok = await handleUserToLesson(lessonData, formValues, fetchCanSignData, selectedFriends, effectiveDuoMode);
    setLoading(false);
    if (ok) onClose();
  };

  const handleSubmitPaid = async () => {
    if (formValues.signInMethod === "karnet" && !formValues.selectedPass) {
      return setError("Proszę wybrać karnet");
    }

    if (!acceptRegulamin) {
      return setError("Proszę zaakceptować Regulamin");
    }

    setLoading(true);
    const ok = await handleUserToLesson(lessonData, formValues, fetchCanSignData, selectedFriends, effectiveDuoMode);
    setLoading(false);

    // `true` = landed on (or already on) the waitlist: nothing to pay yet.
    if (ok === true) {
      onClose();
      return;
    }

    const friendsOwed = (ok && ok.friends_amount_owed) || 0;
    const paysNow = ok && (formValues.signInMethod === "pojedyncza" || friendsOwed > 0);
    if (paysNow) {
      const ownOwed = formValues.signInMethod === "pojedyncza" ? (ok.amount_owed ?? modePrice) : 0;
      setPayment({
        amount: ownOwed + friendsOwed,
        label: ownOwed ? (friendsOwed ? "Koszt zajęć (Ty + znajomi)" : "Koszt zajęć") : "Do zapłaty za znajomych",
      });
      // For payment-only-online lessons, auto-redirect to payment
      if (lessonData?.payment_only_online) {
        await handlePayment(lessonData);
      } else {
        setConfirmationOpen(payDialogOn);
      }
    } else if (ok) {
      onClose();
    }
  };

  const handleSignOut = async () => {
    const success = await handleSignOutLesson(lessonData);
    if (success) onClose();
  };

  const handleRemoveWaitlist = async () => {
    const success = await handleRemoveFromWaitlist(lessonData, fetchCanSignData);
    if (success) onClose();
  };

  // Early returns for simple flows
  if (lessonData?.sign_up_disabled) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
          <Typography variant={isMobile ? "h6" : "h5"} component="span" fontWeight="600" sx={{ display: 'block' }}>
            {header}
          </Typography>
          <Typography variant="body2" component="span" color="text.secondary" sx={{ display: 'block' }}>
            {dateLine}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" icon="🔒" sx={{ borderRadius: 2 }}>
            <Typography variant="body2" fontWeight="500">
              Zapisy na te zajęcia są wyłączone
            </Typography>
            <Typography variant="caption">
              Administrator wyłączył możliwość zapisywania się na te zajęcia. Skontaktuj się ze studiem, aby uzyskać więcej informacji.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={onClose} variant="outlined" fullWidth={isMobile} sx={{ minHeight: 44 }}>
            Zamknij
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (canSignData.has_unpaid_debt) {
    return (
      <UnpaidDebtDialog
        open={open}
        onClose={onClose}
        debtAmount={canSignData.debt_amount}
        debtCount={canSignData.debt_count}
        debtDetails={canSignData.debt_details}
        onPayDebt={() => handlePayDebt(canSignData)}
      />
    );
  }

  if (alreadySignedUp) {
    return (
      <AlreadySignedUpDialog
        open={open}
        onClose={onClose}
        header={header}
        dateLine={dateLine}
        signingOut={signingOut}
        onSignOut={handleSignOut}
        lessonData={lessonData}
        myLesson={alreadySignedUp}
      />
    );
  }

  if (lessonData?.is_free) {
    return (
      <SimpleConfirmDialog
        open={open}
        onClose={onClose}
        header={header}
        dateLine={dateLine}
        body="Te zajęcia są darmowe. Czy chcesz się zapisać?"
        extraContent={
          <>
            {duoModePicker}
            {friendSelector}
          </>
        }
        confirmLabel={submitLabel}
        loading={loading}
        onConfirm={handleFreeLesson}
        acceptRegulamin={acceptRegulamin}
        setAcceptRegulamin={setAcceptRegulamin}
        lessonData={lessonData}
        AcceptRegulaminLabel={AcceptRegulaminLabel}
      />
    );
  }

  if (lessonData?.payment_on_site && !lessonData?.payment_only_online) {
    return (
      <SimpleConfirmDialog
        open={open}
        onClose={onClose}
        header={header}
        dateLine={dateLine}
        body="Płatność za te zajęcia odbywa się na miejscu. Czy chcesz się zapisać?"
        extraContent={simpleExtra("Do zapłaty na miejscu")}
        confirmLabel={submitLabel}
        loading={loading}
        onConfirm={handlePaymentOnSite}
        acceptRegulamin={acceptRegulamin}
        setAcceptRegulamin={setAcceptRegulamin}
        lessonData={lessonData}
        AcceptRegulaminLabel={AcceptRegulaminLabel}
      />
    );
  }

  // Handle payment_only_online lessons without pass
  if (lessonData?.payment_only_online && !lessonData?.is_free && !canSignData.has_pass && !isDuo) {
    return (
      <SimpleConfirmDialog
        open={open}
        onClose={onClose}
        header={header}
        dateLine={dateLine}
        body="Te zajęcia wymagają płatności online. Po zapisaniu się zostaniesz automatycznie przekierowany do płatności."
        extraContent={simpleExtra("Do zapłaty online")}
        confirmLabel={submitLabel}
        loading={loading}
        onConfirm={async () => {
          setLoading(true);
          const ok = await handleUserToLesson(lessonData, formValues, fetchCanSignData, selectedFriends, effectiveDuoMode);
          if (ok === true) {
            onClose();
          } else if (ok) {
            await handlePayment(lessonData);
          }
          setLoading(false);
        }}
        acceptRegulamin={acceptRegulamin}
        setAcceptRegulamin={setAcceptRegulamin}
        lessonData={lessonData}
        AcceptRegulaminLabel={AcceptRegulaminLabel}
      />
    );
  }

  // Main paid flow dialog
  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            m: isMobile ? 2 : 3,
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 1,
          pt: 3,
        }}>
          <Typography variant={isMobile ? "h6" : "h5"} component="span" fontWeight="600" gutterBottom sx={{ display: 'block' }}>
            {header}
          </Typography>
          <Typography variant="body2" component="span" color="text.secondary" sx={{ display: 'block' }}>
            {dateLine}
          </Typography>
          {trainerName && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.5 }}>
              <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" component="span" color="text.secondary" fontWeight="500">
                {trainerName}
              </Typography>
            </Box>
          )}
        </DialogTitle>
        
        {/* Location */}
        {lessonData?.lesson_type?.location && (
          <Box sx={{ px: 3, pb: 2 }}>
            <LessonLocation location={lessonData.lesson_type.location} />
          </Box>
        )}

        {isDataLoading ? (
          <DialogContent>
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Stack>
          </DialogContent>
        ) : (
          <>
            <DialogContent sx={{ pt: 2, pb: 3 }}>
              <Stack spacing={2.5}>
                {/* Event Pass Purchase Option - only show if user doesn't have event pass */}
                {lessonData.event_id && eventPasses.length > 0 && !canSignData.has_pass && (() => {
                  const eventColor = lessonData.event_color || '#ff9800';
                  return (
                    <Card 
                      elevation={0} 
                      sx={{ 
                        border: '2px solid',
                        borderColor: eventColor,
                        bgcolor: `${eventColor}15`,
                        borderRadius: 2
                      }}
                    >
                      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                        <Stack spacing={1.5}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EventIcon sx={{ color: eventColor }} />
                            <Typography variant="body2" fontWeight="600">
                              🎫 Karnet na wydarzenie "{lessonData.event_name}"
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Dla tego wydarzenia dostępny jest karnet eventowy – z jednym karnetem wchodzisz na wszystkie zajęcia! 🎫
                          </Typography>
                          <Button
                            variant="contained"
                            onClick={() => setEventStoreModalOpen(true)}
                            startIcon={<EventIcon />}
                            fullWidth
                            sx={{ 
                              minHeight: 44,
                              bgcolor: eventColor,
                              '&:hover': {
                                bgcolor: eventColor,
                                filter: 'brightness(0.9)',
                              }
                            }}
                          >
                            Kup karnet eventowy
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* Availability and Waitlist Status */}
                {canSignData.duo_booked && (
                  <Alert severity="warning" icon="🔒" sx={{ borderRadius: 2 }}>
                    <Typography variant="body2" fontWeight="500">
                      Te zajęcia są już zarezerwowane
                    </Typography>
                  </Alert>
                )}

                {!canSignData.duo_booked && !canSignData.available && !canSignData.is_on_waitlist && !canSignData.pass_expired && (
                  <Alert severity="warning" icon="⏳" sx={{ borderRadius: 2 }}>
                    <Typography variant="body2" fontWeight="500" gutterBottom>
                      Zajęcia są pełne
                    </Typography>
                    <Typography variant="caption">
                      Możesz dołączyć do listy oczekujących. Otrzymasz powiadomienie, jeśli pojawi się wolne miejsce.
                    </Typography>
                  </Alert>
                )}
                
                {canSignData.is_on_waitlist && (
                  <Alert severity="info" icon="📋" sx={{ borderRadius: 2 }}>
                    <Typography variant="body2" fontWeight="500" gutterBottom>
                      Jesteś na liście oczekujących
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="caption">
                        Pozycja:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {canSignData.waitlist_position}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                      Otrzymasz powiadomienie, jeśli pojawi się wolne miejsce.
                    </Typography>
                  </Alert>
                )}

                {isDuo && hasSpotMode && canSignData.spot_available && !canSignData.whole_available && !canSignData.duo_booked && (
                  <Alert severity="info" sx={{ borderRadius: 2 }} data-testid="duo-spots-only">
                    <Typography variant="body2">
                      Dostępne są tylko pojedyncze miejsca.
                    </Typography>
                  </Alert>
                )}

                {duoModePicker}

                {canSignData.available && friendSelector}

                {/* Sign-in options */}
                {isDuo && canSignData.available && (
                  <Card
                    elevation={0}
                    onClick={() => setFormValues({ ...formValues, signInMethod: "pojedyncza", selectedPass: null })}
                    sx={{
                      backgroundColor: '#f8f9fa',
                      border: formValues.signInMethod === "pojedyncza" && duoPasses.length > 0 ? '2px solid' : '1px solid',
                      borderColor: formValues.signInMethod === "pojedyncza" && duoPasses.length > 0 ? 'primary.main' : '#e0e0e0',
                      cursor: duoPasses.length > 0 ? 'pointer' : 'default',
                    }}
                    data-testid="duo-price-card"
                  >
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {isSpot
                          ? "Rezerwujesz jedno miejsce — płatność jednorazowa"
                          : `Rezerwujesz całe zajęcia (${perBooking} os.) — płatność jednorazowa`}
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" color="primary" data-testid="duo-price">
                        {modePrice} zł
                      </Typography>
                    </CardContent>
                  </Card>
                )}
                {/* Duo/trio takes only its make-up pass: "Odrabianie duo/trio" (one entry = the whole
                    booking) or "Odrabianie miejsca duo/trio" (one entry = one spot). */}
                {isDuo && canSignData.available && duoPasses.length > 0 && (
                  <PassSelector
                    passes={duoPasses}
                    selectedPass={formValues.selectedPass}
                    onPassChange={(passId) =>
                      setFormValues({ ...formValues, selectedPass: passId, signInMethod: "karnet" })
                    }
                  />
                )}
                {!isDuo && (canSignData.available || !canSignData.is_on_waitlist) && (
                  <Box>
                    {selectedFriends.length > 0 && (
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        Za Ciebie
                      </Typography>
                    )}
                    {canSignData.has_pass && !canSignData.pass_expired ? (
                      <PassSelector
                        passes={passes}
                        selectedPass={formValues.selectedPass}
                        onPassChange={(passId) =>
                          setFormValues({
                            ...formValues,
                            selectedPass: passId,
                            signInMethod: "karnet",
                          })
                        }
                      />
                    ) : (
                      <PaymentSelector
                        lessonData={lessonData}
                        signInMethod={formValues.signInMethod}
                        onMethodSelect={(method) =>
                          setFormValues({ ...formValues, signInMethod: method })
                        }
                        onBuyPassClick={() => setStoreModalOpen(true)}
                        passExpired={canSignData.pass_expired}
                      />
                    )}
                  </Box>
                )}

                {showGroupSummary && canSignData.available && (
                  <GroupSummary preview={groupPreview} own={ownSeat} />
                )}

                {error && (
                  <Alert variant="filled" severity="error" sx={{ borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}

                {/* Statute Highlights */}
                {(canSignData.available || !canSignData.is_on_waitlist) && (
                  <StatuteHighlights />
                )}

                {/* Regulamin - Mobile only */}
                {isMobile && (canSignData.available || !canSignData.is_on_waitlist) && (
                  <>
                    <Divider />
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={acceptRegulamin} 
                          onChange={(e) => setAcceptRegulamin(e.target.checked)} 
                        />
                      }
                      label={AcceptRegulaminLabel}
                      sx={{ mx: 0 }}
                    />
                  </>
                )}
              </Stack>
            </DialogContent>

            <DialogActions sx={{ 
              px: 3, 
              pb: 3, 
              gap: 1,
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              {/* Regulamin checkbox - Desktop only */}
              {!isMobile && (canSignData.available || !canSignData.is_on_waitlist) && (
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={acceptRegulamin} 
                      onChange={(e) => setAcceptRegulamin(e.target.checked)} 
                      size="small"
                    />
                  }
                  label={AcceptRegulaminLabel}
                  sx={{ mr: 'auto' }}
                />
              )}
              
              <Box sx={{ 
                display: 'flex', 
                gap: 1,
                width: isMobile ? '100%' : 'auto',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <Button 
                  onClick={onClose} 
                  variant="outlined"
                  fullWidth={isMobile}
                  sx={{ minHeight: 44 }}
                >
                  Anuluj
                </Button>
              
                {canSignData.available && (
                  <Button
                    onClick={handleSubmitPaid}
                    color="success"
                    variant="contained"
                    disabled={
                      loading ||
                      !acceptRegulamin ||
                      !formValues.signInMethod ||
                      (formValues.signInMethod === "karnet" && !formValues.selectedPass)
                    }
                    fullWidth={isMobile}
                    sx={{ minHeight: 44 }}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : submitLabel}
                  </Button>
                )}
              
                {!canSignData.duo_booked && !canSignData.available && !canSignData.is_on_waitlist && !canSignData.pass_expired && (
                  <Button
                    onClick={handleSubmitPaid}
                    color="primary"
                    variant="contained"
                    disabled={loading || !acceptRegulamin}
                    fullWidth={isMobile}
                    sx={{ minHeight: 44 }}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : "Dołącz do listy oczekujących"}
                  </Button>
                )}
              
                {canSignData.is_on_waitlist && (
                  <Button
                    onClick={handleRemoveWaitlist}
                    color="warning"
                    variant="contained"
                    disabled={loading}
                    fullWidth={isMobile}
                    sx={{ minHeight: 44 }}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : "Usuń z listy oczekujących"}
                  </Button>
                )}
              </Box>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Payment confirmation dialog */}
      <PaymentChoiceDialog
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onPayOnline={async () => {
          setConfirmationOpen(false);
          onClose();
          await handlePayment(lessonData);
        }}
        onPayOnSite={async () => {
          setConfirmationOpen(false);
          onClose();
          setNotification({
            message: "Zapisano na zajęcia!",
            type: "success"
          });
          try {
            await markPayOnSite(lessonData.lesson_id);
            await fetchPendingSignups();
          } catch (e) {
            // Non-fatal: the signup already exists; the cash reminder simply
            // won't surface until the next owed refresh.
          }
        }}
        paymentOnlyOnline={lessonData?.payment_only_online}
        lessonData={lessonData}
        amount={payment.amount}
        amountLabel={payment.label}
      />

      {/* Store & login modals */}
      <StoreModal
        open={storeModalOpen}
        onClose={() => setStoreModalOpen(false)}
        lessonCategoryIds={lessonData.lesson_category_ids}
        lessonId={lessonData.lesson_id}
      />
      {/* Event-specific Store Modal */}
      <StoreModal
        open={eventStoreModalOpen}
        onClose={() => setEventStoreModalOpen(false)}
        eventId={lessonData.event_id}
        lessonId={lessonData.lesson_id}
        eventColor={lessonData.event_color}
      />
      <LoginModal open={isLoginOpen} handleClose={() => setIsLoginOpen(false)} />
    </>
  );
};

export default SignUpForLessonModal;
export { payDialogOn } from "./utils/constants";
